import { ApiError } from '@/lib/api';
import type { ZodError } from 'zod';

/**
 * Validation error detail from API
 */
export interface ValidationErrorDetail {
  path: string;
  message: string;
}

/**
 * Field errors object for forms
 * Maps field name to error message
 */
export type FieldErrors = Record<string, string>;

/**
 * Extract field-level validation errors from an ApiError
 * Returns an object mapping field names to error messages
 *
 * @example
 * const fieldErrors = extractFieldErrors(error);
 * // { name: "Name is required", email: "Invalid email format" }
 */
export function extractFieldErrors(error: Error | unknown): FieldErrors {
  if (!(error instanceof ApiError)) {
    return {};
  }

  if (!error.details || !Array.isArray(error.details)) {
    return {};
  }

  const fieldErrors: FieldErrors = {};

  for (const detail of error.details) {
    // Check if detail has the expected structure
    if (
      typeof detail === 'object' &&
      detail !== null &&
      'path' in detail &&
      'message' in detail
    ) {
      const { path, message } = detail as ValidationErrorDetail;
      fieldErrors[path] = message;
    }
  }

  return fieldErrors;
}

/**
 * Check if an error is a validation error (has field-level details)
 */
export function isValidationError(error: Error | unknown): boolean {
  if (!(error instanceof ApiError)) {
    return false;
  }

  return error.code === 'VALIDATION_ERROR' && Array.isArray(error.details) && error.details.length > 0;
}

/**
 * Get a user-friendly error message from an error
 * Falls back to a default message if the error doesn't have a message
 */
export function getErrorMessage(error: Error | unknown, defaultMessage = 'An error occurred'): string {
  if (error instanceof Error) {
    return error.message || defaultMessage;
  }

  return defaultMessage;
}

/**
 * Convert Zod validation errors to field errors object
 * Returns an object mapping field names to error messages
 *
 * @example
 * const result = schema.safeParse(data);
 * if (!result.success) {
 *   const fieldErrors = zodErrorsToFieldErrors(result.error);
 *   // { name: "Name is required", email: "Invalid email format" }
 * }
 */
export function zodErrorsToFieldErrors(error: ZodError): FieldErrors {
  const fieldErrors: FieldErrors = {};

  for (const issue of error.issues) {
    const path = issue.path.join('.');
    fieldErrors[path] = issue.message;
  }

  return fieldErrors;
}
